/**
 * Pure, React-free helpers for the Decision Log domain: id generation,
 * blank/edit draft factories, list parsing, validation, filtering, a
 * client-side free-text search, revisit-due computation, sorting and
 * formatting.
 *
 * Deliberately NO productivity/velocity/leaderboard metric: decisions are
 * recorded for knowledge retention + rationale discovery, never counted or
 * ranked per person.
 */

import { v4 as uuid } from 'uuid';
import type {
  Decision,
  DecisionDraft,
  DecisionFilter,
  DecisionSummary,
} from './types';

/** Deterministic document id for a new decision. */
export function decisionDocId(): string {
  return `dec_${uuid()}`;
}

/** Today's local date as yyyy-mm-dd. */
export function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Creates an empty draft for the create form (optionally seeding fields). */
export function blankDecisionDraft(
  owner = '',
  seed: Partial<DecisionDraft> = {},
): DecisionDraft {
  return {
    decision: '',
    context: '',
    optionsConsidered: [],
    decisionMade: '',
    why: '',
    owner,
    participants: [],
    date: todayIso(),
    affectedServices: [],
    relatedDocId: undefined,
    relatedGoalId: undefined,
    relatedWorkstreamId: undefined,
    relatedStoryIds: [],
    revisitDate: undefined,
    ...seed,
  };
}

/** Projects an existing decision into an editable draft. */
export function draftFromDecision(decision: Decision): DecisionDraft {
  return {
    decision: decision.decision,
    context: decision.context,
    optionsConsidered: decision.optionsConsidered ?? [],
    decisionMade: decision.decisionMade,
    why: decision.why,
    owner: decision.owner,
    participants: decision.participants ?? [],
    date: decision.date,
    affectedServices: decision.affectedServices ?? [],
    relatedDocId: decision.relatedDocId,
    relatedGoalId: decision.relatedGoalId,
    relatedWorkstreamId: decision.relatedWorkstreamId,
    relatedStoryIds: decision.relatedStoryIds ?? [],
    revisitDate: decision.revisitDate,
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved).
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw ?? '').split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/**
 * Parses a NEWLINE-separated string into a list, preserving commas inside each
 * entry (options / participants can contain commas). De-duped, trimmed.
 */
export function parseLines(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw ?? '').split(/\n/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/** Joins a list into a newline-separated string for a textarea field. */
export function listToLines(list?: string[]): string {
  return (list ?? []).join('\n');
}

/**
 * Validates a draft for save. A decision with no headline, no chosen option, or
 * no rationale is not worth recording, so those are required.
 */
export function validateDecisionDraft(draft: DecisionDraft): string[] {
  const problems: string[] = [];
  if (!draft.decision.trim()) problems.push('A decision headline is required.');
  if (!draft.decisionMade.trim()) {
    problems.push('What was decided (the chosen option) is required.');
  }
  if (!draft.why.trim()) {
    problems.push('A "why" is required — the rationale is the point of the log.');
  }
  if (!draft.owner.trim()) problems.push('A decision owner is required.');
  if (!draft.date.trim()) problems.push('A decision date is required.');
  return problems;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole days until the revisit date (negative = overdue). Undefined when none. */
export function revisitInDays(
  decision: Pick<Decision, 'revisitDate'> | Pick<DecisionSummary, 'revisitDate'>,
): number | undefined {
  if (!decision.revisitDate) return undefined;
  const d = new Date(
    decision.revisitDate.length === 10
      ? `${decision.revisitDate}T00:00:00`
      : decision.revisitDate,
  );
  if (Number.isNaN(d.getTime())) return undefined;
  return Math.round((d.getTime() - Date.now()) / MS_PER_DAY);
}

/** True when a decision has a revisit date in the past. */
export function isRevisitDue(
  decision: Pick<Decision, 'revisitDate'> | Pick<DecisionSummary, 'revisitDate'>,
): boolean {
  const d = revisitInDays(decision);
  return d !== undefined && d < 0;
}

/** The 4-digit year of a decision's date, or '' when unparseable. */
export function decisionYear(d: Pick<DecisionSummary, 'date'>): string {
  const m = /^(\d{4})/.exec(d.date ?? '');
  return m ? m[1] : '';
}

/** Unique, sorted set of years present across the given decisions. */
export function collectYears(items: DecisionSummary[]): string[] {
  const set = new Set<string>();
  for (const d of items) {
    const y = decisionYear(d);
    if (y) set.add(y);
  }
  return Array.from(set).sort((a, b) => b.localeCompare(a));
}

/** Unique, sorted set of owner aliases present across the given decisions. */
export function collectOwners(items: DecisionSummary[]): string[] {
  const set = new Set<string>();
  for (const d of items) {
    const t = (d.owner ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Unique, sorted set of affected services across the given decisions. */
export function collectServices(items: DecisionSummary[]): string[] {
  const set = new Set<string>();
  for (const d of items) {
    for (const s of d.affectedServices ?? []) {
      const t = (s ?? '').trim();
      if (t) set.add(t);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Applies the structured filters (empty fields match everything). */
export function filterDecisions(
  items: DecisionSummary[],
  filter: DecisionFilter,
): DecisionSummary[] {
  const owner = filter.owner.trim();
  const service = filter.service.trim();
  const goalId = filter.goalId.trim();
  const year = filter.year.trim();
  const query = filter.query.trim().toLowerCase();
  return items.filter((d) => {
    if (owner && (d.owner ?? '').toLowerCase() !== owner.toLowerCase()) return false;
    if (
      service &&
      !(d.affectedServices ?? []).some((s) => s.toLowerCase() === service.toLowerCase())
    ) {
      return false;
    }
    if (goalId && d.relatedGoalId !== goalId) return false;
    if (year && decisionYear(d) !== year) return false;
    if (query) {
      const hay = [d.decision, d.context, d.why, d.decisionMade]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(query)) return false;
    }
    return true;
  });
}

/** Sorts decisions newest-first (by decision date, then createdAt). */
export function sortDecisions(items: DecisionSummary[]): DecisionSummary[] {
  return items.slice().sort((a, b) => {
    const byDate = (b.date ?? '').localeCompare(a.date ?? '');
    if (byDate !== 0) return byDate;
    return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });
}

/** Formats a yyyy-mm-dd (or ISO) date as a compact local date. */
export function formatDate(date?: string): string {
  if (!date) return '';
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
